import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Especialidades de colaborador (at-62, ADR 0029) — o "sub-tipo" do mecânico (ex: Pintor).
 *
 * A especialidade liga um conjunto de ESTEIRAS a um conjunto de COLABORADORES: quem a tem só pode
 * ser vinculado e trabalhar nas esteiras dela (a elegibilidade é a união das especialidades da
 * pessoa). A imposição é do backend — o que o frontend faz é avisar antes e explicar depois.
 *
 * ⚠️ `regraAtiva` acompanha as respostas de propósito: com ZERO especialidades ativas a regra fica
 * INERTE e o sistema se comporta como antes da at-62. A tela precisa distinguir "a restrição está
 * desligada" de "todos estão cobertos".
 */
export interface EsteiraDaEspecialidade {
  id: string;
  nome: string;
  cor?: string;
}

export interface ColaboradorDaEspecialidade {
  id: string;
  nome: string;
}

export interface Especialidade {
  id: string;
  nome: string;
  ativa: boolean;
  esteiras: EsteiraDaEspecialidade[];
  colaboradores: ColaboradorDaEspecialidade[];
  createdAt: string;
  updatedAt: string;
}

export interface EspecialidadesResponse {
  regraAtiva: boolean;
  especialidades: Especialidade[];
}

export interface EspecialidadePayload {
  nome: string;
  esteiraIds?: string[];
  colaboradorIds?: string[];
  ativa?: boolean;
  /** Aceite do impacto (desvincular e encerrar atividades). Ver `ImpactoEspecialidade`. */
  confirmar?: boolean;
}

/** Uma sessão que será encerrada pela mudança de configuração. */
export interface SessaoAfetada {
  id: string;
  esteiraId: string;
  status: string;
}

/**
 * Um par (colaborador, veículo) que a mudança de configuração deixaria inconsistente. Vem no corpo
 * do `409 ESPECIALIDADE_IMPACTA_TRABALHO` — é o que a tela descreve antes de pedir a confirmação.
 */
export interface ImpactoEspecialidade {
  collaboratorId: string;
  collaboratorName: string | null;
  carId: string;
  plate: string;
  esteiraId: string;
  esteiraNome: string;
  sessoes: SessaoAfetada[];
}

export interface EspecialidadeSalva extends Especialidade {
  /** Pares efetivamente afetados pela mudança (vazio quando não houve). */
  afetados: ImpactoEspecialidade[];
}

export interface CoberturaEspecialidades {
  regraAtiva: boolean;
  /** Esteiras de trabalho que NENHUMA especialidade ativa cobre — ninguém pode trabalhar nelas. */
  esteirasSemCobertura: Array<{ id: string; nome: string }>;
  /** Colaboradores ativos fora de toda especialidade ativa — não podem ser atribuídos a nada. */
  colaboradoresSemEspecialidade: Array<{ id: string; nome: string }>;
  /** Vínculos abertos que a configuração atual não sustenta (herança ou esteira desativada). */
  vinculosInconsistentes: ImpactoEspecialidade[];
}

/**
 * Um colaborador que pode ser vinculado.
 *
 * `viaParalela` (at-99) vem só quando ele **não** atende a esteira consultada e entrou na lista
 * por uma esteira aberta **em paralelo** — com o nome dela. É o que o seletor mostra ao lado do
 * nome (pedido do Victor, 10/09/2026): sem o aviso, o Chefe conclui que a pessoa tem a
 * especialidade da etapa, e ela some da lista sem explicação quando a paralela encerrar.
 */
export interface ColaboradorElegivel {
  id: string;
  fullName: string | null;
  viaParalela?: string[];
}

export interface ElegibilidadeResponse {
  regraAtiva: boolean;
  elegiveis: ColaboradorElegivel[];
}

export async function getEspecialidades(): Promise<EspecialidadesResponse> {
  const response = await axios.get(`${BACKEND_URL}/especialidades`, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

export async function getCoberturaEspecialidades(): Promise<CoberturaEspecialidades> {
  const response = await axios.get(`${BACKEND_URL}/especialidades/cobertura`, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

/**
 * Colaboradores que podem trabalhar na esteira informada.
 *
 * Sem `esteiraId` devolve todos os colaboradores ativos. Falha desta chamada NÃO pode travar a
 * tela: o seletor segue completo e o backend responde 409 se o caso for de bloqueio (fail-open
 * deliberado — o mesmo princípio do bloqueio de pausas da at-48b).
 *
 * 🔴 **`carId` é o que faz o seletor enxergar as esteiras PARALELAS** (at-99, ADR 0050). Omiti-lo
 * num seletor de veículo desabilita quem só tem a especialidade da paralela — e o backend teria
 * aceitado, porque o vínculo avalia o conjunto de esteiras ativas. Todo seletor que já sabe de
 * qual veículo está falando precisa passá-lo.
 */
export async function getColaboradoresElegiveis(
  esteiraId?: string,
  carId?: string,
): Promise<ElegibilidadeResponse> {
  const response = await axios.get(`${BACKEND_URL}/especialidades/elegiveis`, {
    params: {
      ...(esteiraId ? { esteiraId } : {}),
      ...(carId ? { carId } : {}),
    },
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

export async function createEspecialidade(data: EspecialidadePayload): Promise<EspecialidadeSalva> {
  const response = await axios.post(`${BACKEND_URL}/especialidades`, data, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

export async function updateEspecialidade(
  id: string,
  data: Partial<EspecialidadePayload>,
): Promise<EspecialidadeSalva> {
  const response = await axios.patch(`${BACKEND_URL}/especialidades/${id}`, data, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

/** `confirmar` vai na QUERY (o DELETE não leva corpo) e só o literal `true` confirma. */
export async function deleteEspecialidade(
  id: string,
  confirmar = false,
): Promise<{ id: string; removida: boolean; afetados: ImpactoEspecialidade[] }> {
  const response = await axios.delete(`${BACKEND_URL}/especialidades/${id}`, {
    params: confirmar ? { confirmar: 'true' } : undefined,
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

/** Frase única para descrever um item de impacto na confirmação e nos avisos. */
export function descreverImpacto(item: ImpactoEspecialidade): string {
  const nome = item.collaboratorName ?? 'Colaborador';
  const sessoes = item.sessoes?.length ?? 0;
  const base = `${nome} sairá do veículo ${item.plate} (${item.esteiraNome})`;
  if (sessoes === 0) return `${base}.`;
  return (
    `${base} e ${
      sessoes === 1
        ? 'a atividade em aberto dele será encerrada'
        : `as ${sessoes} atividades em aberto dele serão encerradas`
    } — as horas trabalhadas vão para aprovação do Chefe de Oficina.`
  );
}
